import {PAdbContext} from "../PA.Core/PAdbContext";
import {ILease} from "../PA.Utils/PA.UtilsIF";
import * as AsyncLock from 'async-lock'
import * as _ from 'lodash';
import {GenericLease} from "../PA.Utils/GenericLease";
import {LeaseRegistry} from "../PA.Utils/LeaseRegistry";
import {WB_PricingEnginePriceRelRow, WB_PricingEngineRow} from "../PA.Core/PAdbSchemaRows";
import {
    esWB_Pricing_Engine,
    esWB_Pricing_Engine_Price_Relationship
} from "../PA.Core/PAdbSchema";

export class PricingEngineService {

    private static _dbContext: PAdbContext;
    private static _lease: ILease;

    private static dctCndTypeWbIDByEngineWbIDCndTypeID: { [pricingEngineWbID: number]: { [priceType: string]: number } } = {};
    private static dctPricingEngineByWbID: { [wbID: number]: WB_PricingEngineRow } = {};
    private static dctPricingEnginePrByWbID: { [wbID: number]: WB_PricingEnginePriceRelRow } = {};

    private static dctLock: any = new AsyncLock();
    private static bIsLoaded: boolean = false;

    static getConditionTypeWbIDByPricingEnginePriceTypeID(wbPricingEngineID: number, priceType: string) : Promise<number> {
        return new Promise<number>((resolve, error) => {
            PricingEngineService.dctLock.acquire('global',
                (done: any) => {
                    PricingEngineService.checkLease()
                        .then((ignore) => {
                            if (wbPricingEngineID in PricingEngineService.dctCndTypeWbIDByEngineWbIDCndTypeID && priceType in PricingEngineService.dctCndTypeWbIDByEngineWbIDCndTypeID[wbPricingEngineID]) {
                                done(null, PricingEngineService.dctCndTypeWbIDByEngineWbIDCndTypeID[wbPricingEngineID]);
                                resolve(PricingEngineService.dctCndTypeWbIDByEngineWbIDCndTypeID[wbPricingEngineID][priceType]);
                                return;
                            } else if (PricingEngineService.bIsLoaded) {
                                done(null, PricingEngineService.dctCndTypeWbIDByEngineWbIDCndTypeID[wbPricingEngineID]);
                                resolve(undefined);
                                return;
                            }
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
        })
    }

    static getPriceRelByWbID(wbPriceRelID: number) : Promise<WB_PricingEnginePriceRelRow> {
        return new Promise<WB_PricingEnginePriceRelRow>((resolve, error) => {
            PricingEngineService.dctLock.acquire('global',
                (done: any) => {
                    PricingEngineService.checkLease()
                        .then((ignore) => {
                            if (wbPriceRelID in PricingEngineService.dctPricingEnginePrByWbID) {
                                done(null, PricingEngineService.dctPricingEnginePrByWbID[wbPriceRelID]);
                                resolve(PricingEngineService.dctPricingEnginePrByWbID[wbPriceRelID]);
                                return;
                            } else if (PricingEngineService.bIsLoaded && !(wbPriceRelID in PricingEngineService.dctPricingEnginePrByWbID)) {
                                done(null, PricingEngineService.dctPricingEnginePrByWbID[wbPriceRelID]);
                                resolve(undefined);
                                return;
                            }
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
        })
    }

    static getPricingEngineByWbID(wbPricingEngineID: number) : Promise<WB_PricingEngineRow> {
        return new Promise<WB_PricingEngineRow>((resolve, error) => {
            PricingEngineService.dctLock.acquire('global',
                (done: any) => {
                    PricingEngineService.checkLease()
                        .then((ignore) => {
                            if (wbPricingEngineID in PricingEngineService.dctPricingEngineByWbID) {
                                done(null, PricingEngineService.dctPricingEngineByWbID[wbPricingEngineID]);
                                resolve(PricingEngineService.dctPricingEngineByWbID[wbPricingEngineID]);
                                return;
                            } else if (PricingEngineService.bIsLoaded) {
                                done(null, PricingEngineService.dctPricingEngineByWbID[wbPricingEngineID]);
                                resolve(undefined);
                                return;
                            }
                        })
                        .catch((err) => {
                            error(err)
                        });
                })
        })
    }

    private static async loadFromDB(): Promise<void> {
        let pricingEngines: WB_PricingEngineRow[] = await PricingEngineService._dbContext.findAll(esWB_Pricing_Engine, {where: {DELETED_IND: {neq: "1"}}});
        let pricingEnginePriceRels: WB_PricingEnginePriceRelRow[] = await PricingEngineService._dbContext.findAll(esWB_Pricing_Engine_Price_Relationship, {where: {DELETED_IND: {neq: "1"}}});

        PricingEngineService.dctPricingEngineByWbID = _.keyBy(pricingEngines, (aPricingEngine) => {
            return aPricingEngine.cif.WB_PRICINGENGINE_ID
        });
        PricingEngineService.dctPricingEnginePrByWbID = _.keyBy(pricingEnginePriceRels, (aRel) => {
            return aRel.cif.WB_PRICINGENGINEPR_ID
        });

        _.forEach(pricingEnginePriceRels, (aPriceRel) => {
            if (!(aPriceRel.cif.WB_PRICINGENGINE_ID in PricingEngineService.dctCndTypeWbIDByEngineWbIDCndTypeID)) {
                PricingEngineService.dctCndTypeWbIDByEngineWbIDCndTypeID[aPriceRel.cif.WB_PRICINGENGINE_ID] = {};
            }
            PricingEngineService.dctCndTypeWbIDByEngineWbIDCndTypeID[aPriceRel.cif.WB_PRICINGENGINE_ID][aPriceRel.cif.PRICE_TYPE] = aPriceRel.cif.PRD_CNDM_ID;
        });

        PricingEngineService.bIsLoaded = true;
    }

    static async reset(): Promise<void> {
        PricingEngineService.instantiateContext();
        let arr: any[] = [];

        arr = _.values(PricingEngineService.dctPricingEngineByWbID);
        await PricingEngineService._dbContext.purgeCache(esWB_Pricing_Engine, arr);
        PricingEngineService.dctCndTypeWbIDByEngineWbIDCndTypeID = {};

        arr = _.values(PricingEngineService.dctPricingEnginePrByWbID);
        await PricingEngineService._dbContext.purgeCache(esWB_Pricing_Engine_Price_Relationship, arr);
        PricingEngineService.dctPricingEnginePrByWbID = {};

    }

    private static instantiateContext() {
        if (_.isUndefined(PricingEngineService._dbContext)) {
            PricingEngineService._dbContext = new PAdbContext();
        }
    }

    private static async checkLease(): Promise<void> {
        if (_.isUndefined(PricingEngineService._lease)) {
            PricingEngineService._lease = new GenericLease(LeaseRegistry.Lease_Type_PricingEngine);
        }

        if (await PricingEngineService._lease.hasDataChanged() || !PricingEngineService.bIsLoaded) {
            await PricingEngineService.reset();
            await PricingEngineService.loadFromDB();
        }
    }

}